import type {
  CornerEnumKey,
  CustomColorSet,
  EditTarget,
  LayoutBlock,
  LocalBusinessBranding,
  PageMeta,
  SiteMode,
  SpacingEnumKey,
  TypographyEnumKey,
  WorkspaceInteractionMode,
} from '@web-studio/shared-schemas';
import {
  COLORSET_ID,
  composeBlocksForPageView,
  CORNER_ENUM_KEYS,
  PLATFORM_VERTICAL_KEYS,
  SPACING_ENUM_KEYS,
  THEME_ID,
  TYPOGRAPHY_ENUM_KEYS,
} from '@web-studio/shared-schemas';
import { create } from 'zustand';

export interface SandboxPreviewState {
  locale: string;
  interactionMode: WorkspaceInteractionMode;
  activeBlockId: string | null;
  activeEditTarget: EditTarget | null;
  /** Flat composed blocks (legacy + fallback). */
  blocks: LayoutBlock[];
  globalBlocks: LayoutBlock[];
  canvasBlocks: LayoutBlock[];
  activePageId: string | null;
  pages: PageMeta[];
  siteMode: SiteMode;
  vertical: string;
  defaultLocale: string;
  supportedLocales: string[];
  scopedPreview: boolean;
  showInvisibleBlocks: boolean;
  themeId: string;
  colorsetId: string;
  customColorSets: CustomColorSet[];
  spacingEnum: SpacingEnumKey;
  cornerEnum: CornerEnumKey;
  typographyEnum: TypographyEnumKey;
  /** Site-wide tasting timeline (sushi Omakase nav), not just the active page. */
  hasTastingTimeline: boolean;
  /** Persona for render-time PDL hydrate (Imprint / footer bindings). */
  personaBranding: LocalBusinessBranding | null;
  businessName: string;
}

export interface SandboxPreviewActions {
  setLocale: (locale: string) => void;
  setShowInvisibleBlocks: (show: boolean) => void;
  setActiveBlockId: (blockId: string | null, editTarget?: EditTarget | null) => void;
  setInteractionMode: (mode: WorkspaceInteractionMode) => void;
  setBlocks: (
    blocks: LayoutBlock[],
    themeId?: string,
    colorsetId?: string,
    spacingEnum?: SpacingEnumKey,
    cornerEnum?: CornerEnumKey,
    typographyEnum?: TypographyEnumKey,
    persona?: { personaBranding?: LocalBusinessBranding | null; businessName?: string },
    customColorSets?: CustomColorSet[],
  ) => void;
  setScopedBlocks: (input: {
    blocks: LayoutBlock[];
    globalBlocks: LayoutBlock[];
    canvasBlocks: LayoutBlock[];
    activePageId: string;
    pages?: PageMeta[];
    siteMode?: SiteMode;
    vertical?: string;
    defaultLocale?: string;
    supportedLocales?: string[];
    themeId?: string;
    colorsetId?: string;
    customColorSets?: CustomColorSet[];
    spacingEnum?: SpacingEnumKey;
    cornerEnum?: CornerEnumKey;
    typographyEnum?: TypographyEnumKey;
    hasTastingTimeline?: boolean;
    personaBranding?: LocalBusinessBranding | null;
    businessName?: string;
  }) => void;
  patchBlockContent: (blockId: string, key: string, value: string, locale: string) => void;
  patchBlockMedia: (blockId: string, key: string, value: string) => void;
}

function composeFlat(globalBlocks: LayoutBlock[], canvasBlocks: LayoutBlock[], pageId: string): LayoutBlock[] {
  return composeBlocksForPageView(
    { globalBlocks, pageBlocks: { [pageId]: canvasBlocks } },
    pageId,
    { includeHidden: true },
  );
}

function patchBlockList(
  blocks: LayoutBlock[],
  blockId: string,
  key: string,
  value: string,
  locale: string,
): LayoutBlock[] | null {
  const index = blocks.findIndex((block) => block.blockId === blockId);
  if (index === -1) {
    return null;
  }

  const target = blocks[index];
  if (!target) {
    return null;
  }

  const existing = target.content[key] ?? {};
  if (existing[locale] === value) {
    return null;
  }

  const nextBlocks = blocks.slice();
  nextBlocks[index] = {
    ...target,
    content: {
      ...target.content,
      [key]: { ...existing, [locale]: value },
    },
  };
  return nextBlocks;
}

function patchBlockMediaList(
  blocks: LayoutBlock[],
  blockId: string,
  key: string,
  value: string,
): LayoutBlock[] | null {
  const index = blocks.findIndex((block) => block.blockId === blockId);
  const target = blocks[index];
  if (!target || target.media[key] === (value || undefined)) {
    return null;
  }

  const media = { ...target.media };
  if (value) {
    media[key] = value;
  } else {
    delete media[key];
  }

  const nextBlocks = blocks.slice();
  nextBlocks[index] = { ...target, media };
  return nextBlocks;
}

export type SandboxPreviewStore = SandboxPreviewState & SandboxPreviewActions;

export const useSandboxPreviewStore = create<SandboxPreviewStore>()((set, get): SandboxPreviewStore => ({
  locale: 'de',
  interactionMode: 'view',
  activeBlockId: null,
  activeEditTarget: null,
  blocks: [],
  globalBlocks: [],
  canvasBlocks: [],
  activePageId: null,
  pages: [],
  siteMode: 'standard',
  vertical: PLATFORM_VERTICAL_KEYS.restaurant,
  defaultLocale: 'de',
  supportedLocales: ['de'],
  scopedPreview: false,
  showInvisibleBlocks: true,
  themeId: THEME_ID.michelinDark,
  colorsetId: COLORSET_ID.champagneNight,
  customColorSets: [],
  spacingEnum: SPACING_ENUM_KEYS.balanced,
  cornerEnum: CORNER_ENUM_KEYS.rounded,
  typographyEnum: TYPOGRAPHY_ENUM_KEYS.editorialSerif,
  hasTastingTimeline: false,
  personaBranding: null,
  businessName: '',

  setLocale: (locale) => set({ locale }),
  setShowInvisibleBlocks: (show) => set({ showInvisibleBlocks: show }),

  setActiveBlockId: (blockId, editTarget) =>
    set({
      activeBlockId: blockId,
      activeEditTarget: blockId ? (editTarget ?? null) : null,
    }),

  setInteractionMode: (mode) =>
    set({
      interactionMode: mode,
      ...(mode === 'view' ? { activeBlockId: null, activeEditTarget: null } : {}),
    }),

  setBlocks: (
    blocks,
    themeId,
    colorsetId,
    spacingEnum,
    cornerEnum,
    typographyEnum,
    persona,
    customColorSets,
  ) =>
    set((state) => ({
      blocks,
      scopedPreview: false,
      globalBlocks: [],
      canvasBlocks: [],
      activePageId: null,
      pages: [],
      siteMode: 'standard',
      vertical: PLATFORM_VERTICAL_KEYS.restaurant,
      personaBranding:
        persona?.personaBranding !== undefined
          ? persona.personaBranding
          : state.personaBranding,
      businessName:
        persona?.businessName !== undefined ? persona.businessName : state.businessName,
      ...(themeId ? { themeId } : {}),
      ...(colorsetId ? { colorsetId } : {}),
      ...(customColorSets !== undefined ? { customColorSets } : {}),
      ...(spacingEnum ? { spacingEnum } : {}),
      ...(cornerEnum ? { cornerEnum } : {}),
      ...(typographyEnum ? { typographyEnum } : {}),
    })),

  setScopedBlocks: ({
    blocks,
    globalBlocks,
    canvasBlocks,
    activePageId,
    pages,
    siteMode,
    vertical,
    defaultLocale,
    supportedLocales,
    themeId,
    colorsetId,
    customColorSets,
    spacingEnum,
    cornerEnum,
    typographyEnum,
    hasTastingTimeline,
    personaBranding,
    businessName,
  }) =>
    set((state) => ({
      blocks,
      globalBlocks,
      canvasBlocks,
      activePageId,
      pages: pages ?? state.pages,
      siteMode: siteMode ?? state.siteMode,
      vertical: vertical ?? state.vertical,
      defaultLocale: defaultLocale ?? state.defaultLocale,
      supportedLocales: supportedLocales ?? state.supportedLocales,
      scopedPreview: true,
      hasTastingTimeline:
        hasTastingTimeline !== undefined ? hasTastingTimeline : state.hasTastingTimeline,
      // Keep prior Persona when a sync omits it (early handoff before branding loads).
      personaBranding:
        personaBranding !== undefined ? personaBranding : state.personaBranding,
      businessName: businessName !== undefined ? businessName : state.businessName,
      ...(themeId ? { themeId } : {}),
      ...(colorsetId ? { colorsetId } : {}),
      ...(customColorSets !== undefined ? { customColorSets } : {}),
      ...(spacingEnum ? { spacingEnum } : {}),
      ...(cornerEnum ? { cornerEnum } : {}),
      ...(typographyEnum ? { typographyEnum } : {}),
    })),

  patchBlockContent: (blockId, key, value, locale) => {
    const state = get();

    if (state.scopedPreview && state.activePageId) {
      const inGlobal = state.globalBlocks.some((block) => block.blockId === blockId);
      if (inGlobal) {
        const nextGlobal = patchBlockList(state.globalBlocks, blockId, key, value, locale);
        if (!nextGlobal) {
          return;
        }
        set({
          globalBlocks: nextGlobal,
          blocks: composeFlat(nextGlobal, state.canvasBlocks, state.activePageId),
        });
        return;
      }

      const nextCanvas = patchBlockList(state.canvasBlocks, blockId, key, value, locale);
      if (!nextCanvas) {
        return;
      }
      set({
        canvasBlocks: nextCanvas,
        blocks: composeFlat(state.globalBlocks, nextCanvas, state.activePageId),
      });
      return;
    }

    const nextBlocks = patchBlockList(state.blocks, blockId, key, value, locale);
    if (nextBlocks) {
      set({ blocks: nextBlocks });
    }
  },

  patchBlockMedia: (blockId, key, value) => {
    const state = get();

    if (state.scopedPreview && state.activePageId) {
      const inGlobal = state.globalBlocks.some((block) => block.blockId === blockId);
      if (inGlobal) {
        const nextGlobal = patchBlockMediaList(state.globalBlocks, blockId, key, value);
        if (!nextGlobal) {
          return;
        }
        set({
          globalBlocks: nextGlobal,
          blocks: composeFlat(nextGlobal, state.canvasBlocks, state.activePageId),
        });
        return;
      }

      const nextCanvas = patchBlockMediaList(state.canvasBlocks, blockId, key, value);
      if (!nextCanvas) {
        return;
      }
      set({
        canvasBlocks: nextCanvas,
        blocks: composeFlat(state.globalBlocks, nextCanvas, state.activePageId),
      });
      return;
    }

    const nextBlocks = patchBlockMediaList(state.blocks, blockId, key, value);
    if (nextBlocks) {
      set({ blocks: nextBlocks });
    }
  },
}));
